import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAtom, useSetAtom } from "jotai";

import {
  createErrorResultFromException,
  IDateRange,
  IZReport,
  zReportsAtom,
  zReportsCacheKeyAtom,
  zReportsErrorAtom,
  zReportsLoadingAtom,
} from "@prostocheck/shared";

import { fetchZReports } from "../../api";
import { notifyError } from "../../notifications";
import { useSession } from "../use-session";
import { IUseZReports } from "./use-z-reports.models";

/**
 * Fetches a register's Z-reports over `range` and writes them into
 * `zReportsAtom` — nothing else. `range` comes from `useRegisterFilters`, the
 * same form `useReceipts` reads, so both sections of the register page stay
 * in sync. Results and a cache key live in atoms so a remount — e.g. opening
 * a Z-report modal and closing it — skips the network call when the cache
 * key already matches.
 */
export const useZReports = (range: IDateRange): IUseZReports => {
  const { t } = useTranslation();
  const { session } = useSession();

  const setReports = useSetAtom(zReportsAtom);
  const [cacheKey, setCacheKey] = useAtom(zReportsCacheKeyAtom);
  const setIsLoading = useSetAtom(zReportsLoadingAtom);
  const setErrorResult = useSetAtom(zReportsErrorAtom);

  const getZReports = useCallback(
    async (cashRegisterFn: string) => {
      const isCached =
        cacheKey?.cashRegisterFn === cashRegisterFn &&
        cacheKey?.from === range.from &&
        cacheKey?.to === range.to;

      if (isCached) {
        setErrorResult(null);

        return;
      }

      setIsLoading(true);
      setErrorResult(null);

      try {
        // `useSession` already guarantees a session inside RequireSession's routes.
        const result = await fetchZReports({
          client: session!.client,
          cashRegisterFn,
          ...range,
        });

        if (!result.success) {
          setErrorResult(result);
          notifyError({ result, t });

          return;
        }

        setReports(result.data ?? []);
        setCacheKey({ cashRegisterFn, from: range.from, to: range.to });
      } catch (error) {
        const result = createErrorResultFromException<IZReport[]>(error);

        setErrorResult(result);
        notifyError({ result, t });
      } finally {
        setIsLoading(false);
      }
    },
    [
      session,
      range.from,
      range.to,
      cacheKey,
      t,
      setIsLoading,
      setErrorResult,
      setReports,
      setCacheKey,
    ],
  );

  return { getZReports };
};
